// src/app/components/sidebar/sidebar.component.ts
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core'
import { RouterModule } from '@angular/router'
import { CommonModule } from '@angular/common'
import { getSidebarItems } from '../../constants/sidebar-items'
import { SidebarItem } from '../../models/sidebar-item.model'
import { TranslatePipe } from '../../pipes/translatePipe'
import { TranslationService } from '../../services/translation.service'
import { Router, NavigationEnd } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { LogoService } from '../../services/logo.service';
import { filter, Subscription } from 'rxjs';
import { PermissionService } from '../../services/permission-service'

export interface SidebarSection {
  label: string
  items: SidebarItem[]
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterModule, CommonModule, TranslatePipe],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss'
})

export class Sidebar implements OnInit, OnDestroy {
  private static readonly SECTION_ORDER = ['Main', 'Management', 'Tools']
  sidebarItems: SidebarItem[] = []
  sidebarSections: SidebarSection[] = []
  isCollapsed = false
  isMobile = false
  showSidebarOverlay = false
  isRTL = false
  selectedRole: any = null
  private expandedGroups = new Set<string>()

  filteredSidebarItems: SidebarItem[] = []
  private routerSubscription?: Subscription
  private previousIsAdmin: boolean = false
  logoUrl: string = LogoService.DEFAULT_LOGO_PATH
  private logoSubscription?: Subscription
  private boundCheckScreen = () => this.checkScreen()

  constructor(private router: Router,
    private translationService: TranslationService,
    private authService: AuthService,
    private logoService: LogoService,
    private permissionService: PermissionService
  ) {
    this.isCollapsed = localStorage.getItem('sidebarCollapsed') === 'true'
    this.loadSelectedRole()
    this.previousIsAdmin = this.authService.isAdminUser()
    this.loadSidebarItems()
    this.filterSidebarItems()
  }

  ngOnInit() {
    this.checkScreen()
    this.checkRTL()
    window.addEventListener('resize', this.boundCheckScreen)
    window.addEventListener('roleChanged', this.onRoleChanged)
    window.addEventListener('roleRefreshed', this.onRoleRefreshed)

    this.logoUrl = this.logoService.getLogoUrl()
    this.logoSubscription = this.logoService.logoUrl$.subscribe(url => {
      this.logoUrl = url
    })

    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.syncExpandedFromRoute()
      const currentIsAdmin = this.authService.isAdminUser()
      if (currentIsAdmin !== this.previousIsAdmin) {
        this.previousIsAdmin = currentIsAdmin
        this.loadSidebarItems()
        this.filterSidebarItems()
      }
    })
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.boundCheckScreen)
    window.removeEventListener('roleChanged', this.onRoleChanged)
    window.removeEventListener('roleRefreshed', this.onRoleRefreshed)
    this.routerSubscription?.unsubscribe()
    this.logoSubscription?.unsubscribe()
  }

  get iconOnly(): boolean {
    return this.isCollapsed && !this.isMobile
  }

  get isSuperAdmin(): boolean {
    return this.authService.isAdminUser()
  }

  checkScreen() {
    this.isMobile = window.innerWidth <= 991
    if (!this.isMobile) {
      this.showSidebarOverlay = false
    }
  }

  checkRTL() {
    this.isRTL = this.translationService.isRTL()
  }

  toggleSidebar() {
    if (this.isMobile) {
      this.showSidebarOverlay = !this.showSidebarOverlay
    } else {
      this.isCollapsed = !this.isCollapsed
      localStorage.setItem('sidebarCollapsed', String(this.isCollapsed))
    }
  }

  onSidebarItemClick(): void {
    localStorage.removeItem('selectedDepartmentId')
    if (this.isMobile) {
      this.showSidebarOverlay = false
    }
  }

  toggleGroup(item: SidebarItem): void {
    if (this.iconOnly) {
      this.isCollapsed = false
      localStorage.setItem('sidebarCollapsed', 'false')
      this.expandedGroups.add(item.label)
      return
    }
    if (this.expandedGroups.has(item.label)) {
      this.expandedGroups.delete(item.label)
    } else {
      this.expandedGroups.add(item.label)
    }
  }

  isGroupExpanded(item: SidebarItem): boolean {
    return this.expandedGroups.has(item.label)
  }

  isChildActive(item: SidebarItem): boolean {
    const url = this.router.url.split('?')[0]
    return !!item.children?.some(child => this.routeMatches(child.route, url))
  }

  isExternal(route?: string): boolean {
    return !!route && /^https?:\/\//i.test(route)
  }

  exactLink(route?: string): boolean {
    return route === '/dashboard' || route === '/admin/dashboard'
  }

  onNavKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (this.isMobile && this.showSidebarOverlay) {
        this.showSidebarOverlay = false
      }
      return
    }

    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      this.handleGroupArrow(event)
      return
    }

    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      return
    }

    const nav = event.currentTarget as HTMLElement
    const items = Array.from(
      nav.querySelectorAll<HTMLElement>('a.nav-link, button.nav-link')
    ).filter(el => !el.hasAttribute('disabled') && el.getClientRects().length > 0)

    if (!items.length) return

    const currentIndex = items.indexOf(document.activeElement as HTMLElement)
    let nextIndex = currentIndex

    if (event.key === 'Home') nextIndex = 0
    else if (event.key === 'End') nextIndex = items.length - 1
    else if (event.key === 'ArrowDown') nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % items.length
    else nextIndex = currentIndex <= 0 ? items.length - 1 : currentIndex - 1

    event.preventDefault()
    items[nextIndex].focus()
  }

  getSafeId(label: string, type: string): string {
    return `${type}-${label.replace(/\s+/g, '-').toLowerCase()}`
  }

  getExpandIcon(): string {
    if (this.isRTL) {
      return this.isCollapsed ? '../../../../assets/images/expand-left.svg' : '../../../../assets/images/expand-right.svg'
    } else {
      return this.isCollapsed ? '../../../../assets/images/expand-right.svg' : '../../../../assets/images/expand-left.svg'
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isMobile && this.showSidebarOverlay) {
      this.showSidebarOverlay = false
    }
  }

  private handleGroupArrow(event: KeyboardEvent): void {
    const current = document.activeElement as HTMLElement | null
    const label = current?.getAttribute('data-group')
    if (!label) return
    const item = this.filteredSidebarItems.find(entry => entry.label === label)
    if (!item?.children?.length) return

    const openToEnd = this.isRTL ? event.key === 'ArrowLeft' : event.key === 'ArrowRight'
    const isOpen = this.isGroupExpanded(item)
    if (openToEnd === isOpen) return

    event.preventDefault()
    this.toggleGroup(item)
  }

  private onRoleChanged = (e: Event) => {
    localStorage.removeItem('selectedDepartmentId')
    const customEvent = e as CustomEvent
    this.selectedRole = customEvent.detail
    this.permissionService.refreshPermissions()
    const isAdmin = this.authService.isAdminUser()
    this.previousIsAdmin = isAdmin
    const dashboardRoute = isAdmin ? '/admin/dashboard' : '/dashboard'
    // beginRoleChange() (set by the role selector) lets this redirect skip the dirty-form guard.
    this.router.navigate([dashboardRoute])
      .catch(() => false)
      .finally(() => this.authService.endRoleChange())
    this.loadSidebarItems()
    this.filterSidebarItems()
  }

  private onRoleRefreshed = (e: Event) => {
    const customEvent = e as CustomEvent
    this.selectedRole = customEvent.detail
    this.permissionService.refreshPermissions()
    this.loadSidebarItems()
    this.filterSidebarItems()
  }

  private loadSidebarItems() {
    this.sidebarItems = getSidebarItems()
  }

  private loadSelectedRole() {
    const stored = localStorage.getItem('selectedRole')
    if (stored) {
      try {
        this.selectedRole = JSON.parse(stored)
        return
      } catch {
        this.selectedRole = null
      }
    }
    const user = localStorage.getItem('user')
    if (user) {
      try {
        const parsedUser = JSON.parse(user)
        this.selectedRole = parsedUser?.roles?.[0] || null
      } catch {
        this.selectedRole = null
      }
    }
  }

  private routeMatches(route: string | undefined, url: string): boolean {
    if (!route || this.isExternal(route)) return false
    const path = route.split('?')[0]
    return url === path || url.startsWith(path + '/')
  }

  private syncExpandedFromRoute(): void {
    const url = this.router.url.split('?')[0]
    for (const item of this.filteredSidebarItems) {
      if (item.children?.some(child => this.routeMatches(child.route, url))) {
        this.expandedGroups.add(item.label)
      }
    }
  }

  // 'Approver Setup' consolidates the former 'Template Approver' and 'PMR Manager' menu
  // entries into one link, so it must stay visible if the role has either underlying permission.
  // 'Approval Groups' is labelled differently from its backend key ('approver-group'), so the
  // label-derived key would never match.
  private static readonly SIDEBAR_ITEM_PERMISSION_ALIASES: Record<string, string[]> = {
    'Approver Setup': ['template-approver', 'pmr-manager'],
    'Approval Groups': ['approver-group'],
  }

  // Items with no permission key of their own yet — they have no entry in the permission
  // tab, so matching on the label would always fail and hide them. Remove an entry here
  // once the backend exposes a real permission for it.
  private static readonly ALWAYS_VISIBLE_SIDEBAR_ITEMS: string[] = []

  private filterSidebarItems() {
    const hasAccess = (label?: string) => {
      if (!label) return false
      if (Sidebar.ALWAYS_VISIBLE_SIDEBAR_ITEMS.includes(label)) return true
      if (!this.selectedRole?.permissions) return false

      const permissionKeys = Sidebar.SIDEBAR_ITEM_PERMISSION_ALIASES[label] ?? [label.toLowerCase().replace(/\s+/g, '-')]

      return this.selectedRole.permissions.some((p: any) =>
        permissionKeys.includes(p.permission_key) && (p.can_read || p.can_edit || p.can_delete)
      )
    }

    this.filteredSidebarItems = (this.sidebarItems || [])
      .map(item => {
        if (item.children && item.children.length) {
          const children = item.children.filter(c => hasAccess(c.label))
          return { ...item, children } as SidebarItem
        }
        return item
      })
      .filter(item => {
        if (item.children && item.children.length) {
          return item.children.length > 0
        }
        if (item.label === 'Dashboard') return true
        return hasAccess(item.label)
      }) as SidebarItem[]

    this.sidebarSections = this.buildSections(this.filteredSidebarItems)
    this.syncExpandedFromRoute()
  }

  private buildSections(items: SidebarItem[]): SidebarSection[] {
    const grouped = new Map<string, SidebarItem[]>()
    for (const item of items) {
      const section = item.section || 'Main'
      const bucket = grouped.get(section) ?? []
      bucket.push(item)
      grouped.set(section, bucket)
    }

    const ordered = Sidebar.SECTION_ORDER
      .filter(label => grouped.has(label))
      .map(label => ({ label, items: grouped.get(label)! }))

    for (const [label, sectionItems] of grouped) {
      if (!Sidebar.SECTION_ORDER.includes(label)) {
        ordered.push({ label, items: sectionItems })
      }
    }
    return ordered
  }
}
